import React from 'react'
import styled from '@emotion/styled'
import ThemeConsumer from '@/hoc/ThemeConsumer'
import { getThemeColor } from '@/utils/themes'
import { mobile } from '@/utils/media'
import { mq } from '@/utils/helpers'
import { fonts } from '@/utils/preset'

const Root = styled.div``

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const Container = styled.div`
  width: 100%;
  color: ${props => {
    if (props.type === 'contrast') {
      return props.theme.text.contrast
    } else {
      return props.theme.text.default
    }
  }};

  margin-top: ${props => props.modifier === 'small'
    ? '-24px'
    : '-64px'
  };
  @media ${mq.and(mobile)} {
    margin-top: ${props => props.modifier === 'small'
      ? '-16px'
      : '-48px'
    };
  }

  & > * {
    margin-top: ${props => props.modifier === 'small'
      ? '24px'
      : '64px'
    };
    @media ${mq.and(mobile)} {
      margin-top: ${props => props.modifier === 'small'
        ? '16px'
        : '48px'
      };
    }
  }

  > div {
    margin-top: 0;
  }

  h2 {
    font-family: ${fonts.jp};
    font-size: 40px;
    font-weight: bold;
    line-height: 1.5;
    @media ${mq.and(mobile)} {
      font-size: 32px;
    }
  }
  h2 + * {
    margin-top: 24px;
    @media ${mq.and(mobile)} {
      margin-top: 24px;
    }
  }
  
  h3 {
    font-size: 24px;
    font-weight: bold;
    line-height: 1.5;
    @media ${mq.and(mobile)} {
      font-size: 20px;
    }
  }

  h4 {
    font-family: ${fonts.jp};
    font-size: 20px;
    font-weight: bold;
    line-height: 1.5;
    @media ${mq.and(mobile)} {
      font-size: 18px;
    }
  }

  h5 {
    font-family: ${fonts.jp};
    font-size: 16px;
    font-weight: bold;
    line-height: 1.5;
    @media ${mq.and(mobile)} {
      font-size: 14px;
    }
  }

  h3 + *,
  h4 + * {
    margin-top: ${props => props.modifier === 'small'
      ? '8px'
      : '32px'
    };;
    @media ${mq.and(mobile)} {
      margin-top: ${props => props.modifier === 'small'
        ? '6px'
        : '24px'
      };
    }
  }

  h5 + *,
  h6 + * {
    margin-top: ${props => props.modifier === 'small'
      ? '8px'
      : '16px'
    };;
    @media ${mq.and(mobile)} {
      margin-top: ${props => props.modifier === 'small'
        ? '6px'
        : '12px'
      };
    }
  }

  table {
    width: 100%;
    &.layout-auto {
      table-layout: fixed;
    }

    thead {
      tr {
        border-color: ${props => props.theme.secondary.base};
      }
      th, td {
        padding-top: 0;
        color: ${props => props.theme.text.default};
      }
      th {
        white-space: nowrap;
      }
    }
    tbody {
      th {
        text-align: left;
      }
    }
    tr {
      border-bottom: 1px solid ${props => props.theme.text.default};

      > * {
        & + th, & + td {
          @media ${mq.with(mobile)} {
            padding-right: 0;
            padding-left: 8px;
          }
        }
        & + td {
          font-weight: normal;
        }
      }
    }
    &.no-head tr:first-of-type {
      border-top: 1px solid ${props => props.theme.text.default};
    }
    th, td {
      width: 50%;
      padding: 16px 24px;
      font-size: 14px;
      line-height: 1.5;
      @media ${mq.and(mobile)} {
        font-size: 12px;
        padding: 16px 8px 16px 0;
      }

      p {
        font-size: inherit;
      }

      > * {
        margin: 8px 0;
      }

      @media ${mq.with(mobile)} {
        &.autowidth-mobile {
          width: auto !important;
        }
        &.nowrap-mobile {
          white-space: nowrap !important;
        }
      }
    }
    th {
      font-weight: bold;
    }
    td {
      font-weight: bold;
      @media ${mq.and(mobile)} {
        width: auto;
        white-space: unset;
      }
    }
  }

  ul,
  ul ul,
  ol ul {
    > li {
      position: relative;
      margin-top: 12px;

      &::before {
        content: '';
        position: absolute;
        top: 7px;
        left: -2.5px;
        display: block;
        width: 6px;
        height: 6px;
        border-radius: 3px;
        background-color: ${props => props.theme.secondary.base};
      }
      position: relative;
      padding-left: 16px;
      font-size: 16px;
      line-height: 1.5;
      @media ${mq.and(mobile)} {
        font-size: 14px;
      }

      &:empty::before {
        display: none;
      }
    }
  }

  ol,
  ul ol,
  ol ol {
    counter-reset: number;

    > li {
      position: relative;
      margin-top: 12px;
      color: ${props => props.theme.secondary.base};

      &::before {
        counter-increment: number;
        content: counter(number) '. ';
        position: absolute;
        top: 0;
        left: 10px;
        display: block;
        font-weight: bold;
        line-height: 1.5;
        color: currentColor;
      }
      position: relative;
      padding-left: 32px;
      font-size: 16px;
      line-height: 1.5;
      @media ${mq.and(mobile)} {
        font-size: 14px;
      }

      &:empty::before {
        display: none;
      }
    }
  }

  table {
    li {
      font-size: 14px;
      @media ${mq.and(mobile)} {
        font-size: 12px;
      }
    }
  }

  p {
    font-family: ${fonts.jp};
    font-size: 16px;
    line-height: 1.75;
    @media ${mq.and(mobile)} {
      font-size: 14px;
    }
  }

  hr {
    display: block;
    height: 1px;
    background-color: currentColor;
  }

  strong {
    font-weight: bold;
    background: linear-gradient(
      transparent 90%,
      ${props => props.theme.secondary.base} 0,
      ${props => props.theme.secondary.base} 0
    );
  }
  
  small {
  }

  em {
    font-style: italic;
  }

  a {
    text-decoration: underline;
    font-weight: bold;
    color: ${props => getThemeColor('primary.light', props.theme)};
  }

  .gatsby-resp-image-wrapper {
    margin: 24px 0;
    max-width: 640px !important;
    margin-left: auto;
    margin-right: auto;
    @media ${mq.and(mobile)} {
      margin: 16px 0;
    }
  }

  .gatsby-resp-image-image {
    box-shadow: none !important;
  }

  .gatsby-resp-iframe-wrapper {
    margin-top: 24px;
    @media ${mq.and(mobile)} {
      margin-top: 16px;
    }
  }
`

const Component = props => {
  const { theme, html, modifier, ...other } = props
  return (
    <Root {...other} >
      <Wrapper>
        <Container modifier={modifier} dangerouslySetInnerHTML={{ __html: props.html }} theme={theme} />
      </Wrapper>
    </Root>
  )
}
export default ThemeConsumer(Component)
